// Página de listagem de produtos
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { getProdutos, createProduto, updateProduto, deleteProduto } from '../../../services/produtosService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import type { Produto } from '../../../types';

export function Produtos() {
  const { empresaSelecionada, contaId } = useEmpresa();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (empresaSelecionada) {
      carregarProdutos();
    }
  }, [empresaSelecionada]);

  const carregarProdutos = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getProdutos(empresaSelecionada.id);
      setProdutos(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar produtos');
      console.error('Erro ao carregar produtos:', err);
    } finally {
      setLoading(false);
    }
  };

  const produtosFiltrados = produtos.filter(p =>
    p.descricao.toLowerCase().includes(busca.toLowerCase()) ||
    p.codigo.toLowerCase().includes(busca.toLowerCase()) ||
    p.categoria.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (produto?: Produto) => {
    setProdutoSelecionado(produto || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setProdutoSelecionado(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir produtos');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir este produto?')) {
      return;
    }

    try {
      await deleteProduto(id);
      await carregarProdutos();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir produto');
      console.error('Erro ao excluir produto:', err);
    }
  };

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Produtos' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar os produtos</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Produtos' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Produtos' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Produtos</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      <Card>
        <Input
          label="Buscar Produto"
          placeholder="Código, descrição ou categoria"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>
      
      <Card>
        <Table
          data={produtosFiltrados}
          columns={[
            { key: 'codigo', label: 'Código' },
            { key: 'descricao', label: 'Descrição' },
            { key: 'categoria', label: 'Categoria' },
            { key: 'precoVenda', label: 'Preço Venda', render: (item) => formatCurrency(item.precoVenda) },
            { key: 'estoque', label: 'Estoque', render: (item) => item.estoque || 0 },
            { key: 'createdAt', label: 'Cadastrado em', render: (item) => formatDate(item.createdAt) },
            {
              key: 'actions',
              label: 'Ações',
              render: (item) => (
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button onClick={() => abrirModal(item)} className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      
      <Modal isOpen={isModalOpen} onClose={fecharModal} title={produtoSelecionado ? 'Editar Produto' : 'Novo Produto'} size="lg">
        <ProdutoForm 
          produto={produtoSelecionado} 
          onClose={fecharModal}
          onSuccess={carregarProdutos}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      </Modal>
    </div>
  );
}

function ProdutoForm({ 
  produto, 
  onClose, 
  onSuccess,
  isSaving,
  setIsSaving
}: { 
  produto: Produto | null; 
  onClose: () => void;
  onSuccess: () => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
}) {
  const { empresaSelecionada, contaId } = useEmpresa();
  const [formData, setFormData] = useState({
    codigo: produto?.codigo || '',
    descricao: produto?.descricao || '',
    categoria: produto?.categoria || '',
    precoVenda: produto?.precoVenda || 0,
    precoCompra: produto?.precoCompra || 0,
    unidadeMedida: produto?.unidadeMedida || 'UN',
    ncm: produto?.ncm || '',
    cest: produto?.cest || '',
    estoque: produto?.estoque || 0,
    imagem: produto?.imagem || '',
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaSelecionada || !contaId) {
      setError('Empresa não selecionada ou sem conta vinculada');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const produtoData = {
        codigo: formData.codigo,
        descricao: formData.descricao,
        categoria: formData.categoria,
        precoVenda: formData.precoVenda,
        precoCompra: formData.precoCompra,
        unidadeMedida: formData.unidadeMedida,
        ncm: formData.ncm || undefined,
        cest: formData.cest || undefined,
        estoque: formData.estoque || undefined,
        imagem: formData.imagem || undefined,
      };

      if (produto) {
        await updateProduto(produto.id, produtoData);
      } else {
        await createProduto(produtoData, contaId, empresaSelecionada.id);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar produto');
      console.error('Erro ao salvar produto:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Código"
          value={formData.codigo}
          onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
          required
          disabled={isSaving}
        />
        <div className="md:col-span-2">
          <Input
            label="Descrição"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            required
            disabled={isSaving}
          />
        </div>
        <Input
          label="Categoria"
          value={formData.categoria}
          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Unidade de Medida"
          value={formData.unidadeMedida}
          onChange={(e) => setFormData({ ...formData, unidadeMedida: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Preço de Venda"
          type="number"
          step="0.01"
          value={formData.precoVenda}
          onChange={(e) => setFormData({ ...formData, precoVenda: parseFloat(e.target.value) || 0 })}
          required
          disabled={isSaving}
        />
        <Input
          label="Preço de Compra"
          type="number"
          step="0.01"
          value={formData.precoCompra}
          onChange={(e) => setFormData({ ...formData, precoCompra: parseFloat(e.target.value) || 0 })}
          required
          disabled={isSaving}
        />
        <Input
          label="NCM"
          value={formData.ncm}
          onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
          disabled={isSaving}
        />
        <Input
          label="CEST"
          value={formData.cest}
          onChange={(e) => setFormData({ ...formData, cest: e.target.value })}
          disabled={isSaving}
        />
        <Input
          label="Estoque"
          type="number"
          value={formData.estoque}
          onChange={(e) => setFormData({ ...formData, estoque: parseInt(e.target.value) || 0 })}
          disabled={isSaving}
        />
        <Input
          label="URL da Imagem"
          type="url"
          value={formData.imagem}
          onChange={(e) => setFormData({ ...formData, imagem: e.target.value })}
          disabled={isSaving}
        />
      </div>
      
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
